import type { AttendanceItem } from "views/Attendance/types"
import type { RequestLeaveItem } from "views/RequestLeave/types"

import { RequestLeaveAction, RequestLeaveStatus } from "__generated__/graphql"
import {
  CheckCircle,
  CrisisAlert,
  Logout,
  OpenInNew,
  RocketLaunch,
  SettingsRemote,
  WatchLater,
  WatchLaterOutlined,
} from "@mui/icons-material"
import { Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { DataViewCalendar, Flex, FlexColumn } from "components/common"
import { DateTime, Interval } from "luxon"
import { useMemo, useState } from "react"
import { getCalendarDateRange } from "utils/dateRange"
import AttendanceDetailDrawer from "views/Attendance/AttendanceDetailDrawer"
import {
  calculateWorkingTime,
  getFirstCheckIn,
  getLastCheckOut,
  isCheckinLate,
  sortAttendancesByActionTime,
} from "views/Attendance/utils"
import { REQUEST_ACTION_LABELS, REQUEST_STATUS_COLORS } from "views/RequestLeave/constants"

type Props = {
  attendances: AttendanceItem[]
  attendeeName?: null | string
  endDate: DateTime
  leaves: RequestLeaveItem[]
  startDate: DateTime
}

const dayKey = (day: DateTime) => day.toFormat("yyyy-MM-dd")
const formatTime = (time: DateTime | null) => (time ? time.toFormat("HH:mm") : "—")

const getRequestActionIcon = (action: RequestLeaveAction) => {
  switch (action) {
    case RequestLeaveAction.RequestLate:
      return <WatchLater fontSize="small" />
    case RequestLeaveAction.RequestLeave:
      return <Logout fontSize="small" />
    case RequestLeaveAction.RequestLeaveEarly:
      return <RocketLaunch fontSize="small" />
    case RequestLeaveAction.RequestRemote:
      return <SettingsRemote fontSize="small" />
    default:
      return null
  }
}

const AttendanceCalendar = ({ attendances, attendeeName, endDate, leaves, startDate }: Props) => {
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(null)

  const days = useMemo(() => getCalendarDateRange(startDate, endDate), [endDate, startDate])

  const attendancesByDay = useMemo(() => {
    const map = new Map<string, AttendanceItem[]>()
    attendances.forEach((attendance) => {
      const records = map.get(attendance.date) ?? []
      records.push(attendance)
      map.set(attendance.date, records)
    })
    return map
  }, [attendances])

  const leavesByDay = useMemo(() => {
    const map = new Map<string, RequestLeaveItem[]>()

    leaves.forEach((leave) => {
      if (leave.status === RequestLeaveStatus.Rejected) return

      const interval = Interval.fromDateTimes(
        DateTime.fromISO(leave.startDate).startOf("day"),
        DateTime.fromISO(leave.endDate).endOf("day"),
      )

      days.forEach((day) => {
        if (!interval.contains(day)) return
        const key = dayKey(day)
        const records = map.get(key) ?? []
        records.push(leave)
        map.set(key, records)
      })
    })

    return map
  }, [days, leaves])

  const selectedAttendances = selectedDate ? attendancesByDay.get(dayKey(selectedDate)) ?? [] : []
  const selectedLeaves = selectedDate ? leavesByDay.get(dayKey(selectedDate)) ?? [] : []

  return (
    <FlexColumn flex={1} gap={2} minHeight={0}>
      <DataViewCalendar
        endDate={endDate}
        getDayAriaLabel={(day) => `Xem chi tiết chấm công ngày ${day.toFormat("dd/MM/yyyy")}`}
        onDayClick={setSelectedDate}
        renderDay={(day) => {
          const key = dayKey(day)
          const dayAttendances = sortAttendancesByActionTime(attendancesByDay.get(key) ?? [])
          const dayLeaves = leavesByDay.get(key) ?? []
          const workingMinutes = calculateWorkingTime(dayAttendances)
          const visibleLeaves = dayLeaves.slice(0, 1)

          return (
            <>
              <IconButton
                aria-label={`Mở chi tiết ngày ${day.toFormat("dd/MM/yyyy")}`}
                onClick={(event) => {
                  event.stopPropagation()
                  setSelectedDate(day)
                }}
                size="small"
                sx={{ position: "absolute", right: 4, top: 4 }}
              >
                <OpenInNew fontSize="inherit" />
              </IconButton>
              <Typography fontWeight={600} variant="caption">
                {day.day}
              </Typography>

              <Stack gap={0.75} overflow="hidden">
                <Flex alignItems="center" gap={0.5} minHeight={24}>
                  {isCheckinLate(dayAttendances) && (
                    <Tooltip arrow title="Đi làm muộn">
                      <CrisisAlert color="error" fontSize="small" />
                    </Tooltip>
                  )}
                  {visibleLeaves.map((leave) => (
                    <Tooltip arrow key={leave.id} title={leave.reason || REQUEST_ACTION_LABELS[leave.action]}>
                      <Chip
                        color={REQUEST_STATUS_COLORS[leave.status]}
                        icon={getRequestActionIcon(leave.action) ?? undefined}
                        label={REQUEST_ACTION_LABELS[leave.action]}
                        size="small"
                        sx={{ maxWidth: "100%" }}
                        variant="outlined"
                      />
                    </Tooltip>
                  ))}
                  {dayLeaves.length > visibleLeaves.length && (
                    <Typography color="text.secondary" variant="caption">
                      +{dayLeaves.length - visibleLeaves.length}
                    </Typography>
                  )}
                </Flex>

                {dayAttendances.length > 0 && (
                  <>
                    <Chip
                      color="success"
                      icon={<CheckCircle />}
                      label={`Vào ${formatTime(getFirstCheckIn(dayAttendances))}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      color="error"
                      label={`Ra ${formatTime(getLastCheckOut(dayAttendances))}`}
                      size="small"
                      variant="outlined"
                    />
                  </>
                )}
                {workingMinutes > 0 && (
                  <Chip
                    color="primary"
                    icon={<WatchLaterOutlined />}
                    label={`${(workingMinutes / 60).toFixed(2)} giờ`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            </>
          )
        }}
        startDate={startDate}
      />

      <AttendanceDetailDrawer
        attendances={selectedAttendances}
        attendeeName={attendeeName}
        date={selectedDate}
        leaves={selectedLeaves}
        onClose={() => setSelectedDate(null)}
      />
    </FlexColumn>
  )
}

export default AttendanceCalendar
